export enum ErrorCode {
  // Network
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  RATE_LIMIT = 'RATE_LIMIT',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  
  // Auth
  AUTH_REQUIRED = 'AUTH_REQUIRED',
  AUTH_EXPIRED = 'AUTH_EXPIRED',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  
  // AI/Generation
  AI_GENERATION_FAILED = 'AI_GENERATION_FAILED',
  AI_UNAVAILABLE = 'AI_UNAVAILABLE',
  AI_TIMEOUT = 'AI_TIMEOUT',
  CONTENT_FILTER = 'CONTENT_FILTER',
  
  // Data
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SAVE_FAILED = 'SAVE_FAILED',
  DELETE_FAILED = 'DELETE_FAILED',
  DUPLICATE_ERROR = 'DUPLICATE_ERROR',
  
  // Generic
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  OPERATION_CANCELLED = 'OPERATION_CANCELLED',
  MAINTENANCE = 'MAINTENANCE'
}

export class UserMessages {
  private static messages: Map<ErrorCode, string> = new Map([
    // Network
    [ErrorCode.NETWORK_ERROR, 'Connection issue. Please check your internet and try again.'],
    [ErrorCode.TIMEOUT, 'This is taking longer than expected. Please try again.'],
    [ErrorCode.RATE_LIMIT, 'Too many requests. Please wait a moment and try again.'],
    [ErrorCode.SERVICE_UNAVAILABLE, 'Service is temporarily unavailable. Please try again later.'],
    
    // Auth
    [ErrorCode.AUTH_REQUIRED, 'Please sign in to continue.'],
    [ErrorCode.AUTH_EXPIRED, 'Your session has expired. Please sign in again.'],
    [ErrorCode.PERMISSION_DENIED, "You don't have permission to do this."],
    [ErrorCode.INVALID_CREDENTIALS, 'Invalid credentials. Please try again.'],
    
    // AI/Generation
    [ErrorCode.AI_GENERATION_FAILED, 'Problem generation failed. Please try again.'],
    [ErrorCode.AI_UNAVAILABLE, 'AI service is temporarily unavailable. Please try again later.'],
    [ErrorCode.AI_TIMEOUT, 'AI generation is taking too long. Please try a simpler request.'],
    [ErrorCode.CONTENT_FILTER, 'Content could not be generated due to safety filters.'],
    
    // Data
    [ErrorCode.NOT_FOUND, "We couldn't find what you're looking for."],
    [ErrorCode.VALIDATION_ERROR, 'Please check your input and try again.'],
    [ErrorCode.SAVE_FAILED, 'Failed to save. Please try again.'],
    [ErrorCode.DELETE_FAILED, 'Failed to delete. Please try again.'],
    [ErrorCode.DUPLICATE_ERROR, 'This item already exists.'],
    
    // Generic
    [ErrorCode.UNKNOWN_ERROR, 'Something went wrong. Please try again.'],
    [ErrorCode.OPERATION_CANCELLED, 'Operation was cancelled.'],
    [ErrorCode.MAINTENANCE, 'System is under maintenance. Please try again later.']
  ]);

  static getMessage(code: ErrorCode, fallback?: string): string {
    return UserMessages.messages.get(code) || fallback || UserMessages.messages.get(ErrorCode.UNKNOWN_ERROR)!;
  }

  static fromError(error: any): { userMessage: string; errorCode: ErrorCode; technical?: string } {
    // Extract technical message
    const technical = error?.message || error?.toString() || 'Unknown error';

    // Map HTTP status codes
    if (error.status || error.response?.status) {
      const status = error.status || error.response?.status;
      switch (status) {
        case 401: 
          return { 
            userMessage: this.getMessage(ErrorCode.AUTH_REQUIRED), 
            errorCode: ErrorCode.AUTH_REQUIRED,
            technical 
          };
        case 403: 
          return { 
            userMessage: this.getMessage(ErrorCode.PERMISSION_DENIED), 
            errorCode: ErrorCode.PERMISSION_DENIED,
            technical 
          };
        case 404: 
          return { 
            userMessage: this.getMessage(ErrorCode.NOT_FOUND), 
            errorCode: ErrorCode.NOT_FOUND,
            technical 
          };
        case 429: 
          return { 
            userMessage: this.getMessage(ErrorCode.RATE_LIMIT), 
            errorCode: ErrorCode.RATE_LIMIT,
            technical 
          };
        case 500:
        case 502:
          return { 
            userMessage: this.getMessage(ErrorCode.SERVICE_UNAVAILABLE), 
            errorCode: ErrorCode.SERVICE_UNAVAILABLE,
            technical 
          };
        case 503: 
          return { 
            userMessage: this.getMessage(ErrorCode.AI_UNAVAILABLE), 
            errorCode: ErrorCode.AI_UNAVAILABLE,
            technical 
          };
        case 504: 
          return { 
            userMessage: this.getMessage(ErrorCode.TIMEOUT), 
            errorCode: ErrorCode.TIMEOUT,
            technical 
          };
      }
    }

    // Map error codes
    if (error.code) {
      switch (error.code) {
        case 'NETWORK_ERROR':
        case 'ENOTFOUND':
        case 'ECONNREFUSED':
          return { 
            userMessage: this.getMessage(ErrorCode.NETWORK_ERROR), 
            errorCode: ErrorCode.NETWORK_ERROR,
            technical 
          };
        case 'ETIMEDOUT':
        case 'TIMEOUT':
          return { 
            userMessage: this.getMessage(ErrorCode.TIMEOUT), 
            errorCode: ErrorCode.TIMEOUT,
            technical 
          };
        case 'AUTH_REQUIRED':
          return { 
            userMessage: this.getMessage(ErrorCode.AUTH_REQUIRED), 
            errorCode: ErrorCode.AUTH_REQUIRED,
            technical 
          };
      }
    }

    // Map error messages
    const message = error?.message?.toLowerCase() || '';
    
    if (message.includes('network') || message.includes('fetch')) {
      return { 
        userMessage: this.getMessage(ErrorCode.NETWORK_ERROR), 
        errorCode: ErrorCode.NETWORK_ERROR,
        technical 
      };
    }

    if (message.includes('timeout')) {
      return { 
        userMessage: this.getMessage(ErrorCode.TIMEOUT), 
        errorCode: ErrorCode.TIMEOUT,
        technical 
      };
    }

    if (message.includes('unauthorized') || message.includes('auth')) {
      return { 
        userMessage: this.getMessage(ErrorCode.AUTH_REQUIRED), 
        errorCode: ErrorCode.AUTH_REQUIRED,
        technical 
      };
    }

    if (message.includes('permission') || message.includes('forbidden')) {
      return { 
        userMessage: this.getMessage(ErrorCode.PERMISSION_DENIED), 
        errorCode: ErrorCode.PERMISSION_DENIED,
        technical 
      };
    }

    if (message.includes('not found')) {
      return { 
        userMessage: this.getMessage(ErrorCode.NOT_FOUND), 
        errorCode: ErrorCode.NOT_FOUND,
        technical 
      };
    }

    if (message.includes('validation') || message.includes('invalid')) {
      return { 
        userMessage: this.getMessage(ErrorCode.VALIDATION_ERROR), 
        errorCode: ErrorCode.VALIDATION_ERROR,
        technical 
      };
    }

    if (message.includes('duplicate') || message.includes('already exists')) {
      return { 
        userMessage: this.getMessage(ErrorCode.DUPLICATE_ERROR), 
        errorCode: ErrorCode.DUPLICATE_ERROR,
        technical 
      };
    }

    if (message.includes('ai') || message.includes('generation')) {
      return { 
        userMessage: this.getMessage(ErrorCode.AI_GENERATION_FAILED), 
        errorCode: ErrorCode.AI_GENERATION_FAILED,
        technical 
      };
    }

    return { 
      userMessage: this.getMessage(ErrorCode.UNKNOWN_ERROR), 
      errorCode: ErrorCode.UNKNOWN_ERROR,
      technical 
    };
  }

  // Helper method for displaying errors in UI components
  static getErrorDisplay(error: any): {
    title: string;
    message: string;
    technical?: string;
    errorCode: ErrorCode;
  } {
    const { userMessage, errorCode, technical } = this.fromError(error);
    
    const titleMap: Partial<Record<ErrorCode, string>> = {
      [ErrorCode.NETWORK_ERROR]: 'Connection Problem',
      [ErrorCode.AUTH_REQUIRED]: 'Authentication Required',
      [ErrorCode.AUTH_EXPIRED]: 'Session Expired',
      [ErrorCode.PERMISSION_DENIED]: 'Access Denied',
      [ErrorCode.NOT_FOUND]: 'Not Found',
      [ErrorCode.AI_UNAVAILABLE]: 'AI Service Issue',
      [ErrorCode.VALIDATION_ERROR]: 'Invalid Input',
      [ErrorCode.UNKNOWN_ERROR]: 'Unexpected Error'
    };

    return {
      title: titleMap[errorCode] || 'Error',
      message: userMessage,
      technical: process.env.NODE_ENV === 'development' ? technical : undefined,
      errorCode
    };
  }
}